// Browser-only helper that captures UTM parameters from the landing URL and
// stashes them in sessionStorage so signup + checkout flows can attach them
// to user metadata, Stripe customer/subscription metadata, and analytics.
//
// Mirrors the pattern in src/lib/promoParam.ts and src/lib/refParam.ts.
//
// Captured keys: utm_source, utm_medium, utm_campaign, utm_content, utm_term.
// Capture is first-touch: once a value is stored for a key, subsequent visits
// without that param do NOT overwrite it (so a user clicking an ad and then
// later visiting organically still attributes to the ad).

const KEY = "knowmeno_utm";

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

// UTM values in the wild can include letters, digits, spaces, dashes,
// underscores, dots, slashes, pipes, plus, percent-encoded chars. Stripe
// metadata values must be <= 500 chars. Clamp defensively.
function sanitizeValue(v: string): string | null {
  const trimmed = v.trim();
  if (!trimmed) return null;
  if (trimmed.length > 200) return trimmed.slice(0, 200);
  return trimmed;
}

export function captureUtmParams(): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    let existing: UtmParams = {};
    try {
      const raw = window.sessionStorage.getItem(KEY);
      if (raw) existing = JSON.parse(raw) as UtmParams;
    } catch {
      existing = {};
    }

    let changed = false;
    const next: UtmParams = { ...existing };
    for (const key of UTM_KEYS) {
      const raw = params.get(key);
      if (raw == null) continue;
      const clean = sanitizeValue(raw);
      if (!clean) continue;
      // First-touch wins: don't overwrite an already-stored value.
      if (!next[key]) {
        next[key] = clean;
        changed = true;
      }
    }
    if (changed) {
      window.sessionStorage.setItem(KEY, JSON.stringify(next));
    }
  } catch {
    // ignore
  }
}

export function getPendingUtm(): UtmParams {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as UtmParams;
    // Strip anything unexpected so we never forward garbage into Stripe metadata.
    const out: UtmParams = {};
    for (const key of UTM_KEYS) {
      const v = parsed?.[key];
      if (typeof v === "string" && v.length > 0) out[key] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export function clearPendingUtm(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
